import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  DatePicker, 
  Select, 
  message, 
  Popconfirm,
  Card,
  Typography,
  Tag,
  Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CalendarOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { giftService, Event, CreateEventRequest, UpdateEventRequest } from '../../services/giftService';
import dayjs from 'dayjs';
import styles from './EventsPage.module.css';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [form] = Form.useForm();

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await giftService.getEvents();
      setEvents(response.data);
    } catch (error) {
      message.error('Không thể tải danh sách sự kiện');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleCreate = () => {
    setEditingEvent(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Event) => {
    setEditingEvent(record);
    form.setFieldsValue({
      name: record.name,
      event_date: dayjs(record.event_date),
      event_type: record.event_type,
      description: record.description
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await giftService.deleteEvent(id);
      message.success('Xóa sự kiện thành công');
      fetchEvents();
    } catch (error) {
      message.error('Không thể xóa sự kiện');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        event_date: values.event_date.format('YYYY-MM-DD')
      };

      if (editingEvent) {
        await giftService.updateEvent(editingEvent.id, data);
        message.success('Cập nhật sự kiện thành công');
      } else {
        await giftService.createEvent(data);
        message.success('Tạo sự kiện thành công');
      }

      setModalVisible(false);
      fetchEvents();
    } catch (error) {
      message.error('Có lỗi xảy ra');
    }
  };

  const getEventTypeLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      wedding: 'Đám cưới',
      housewarming: 'Tân gia',
      birthday: 'Sinh nhật',
      graduation: 'Tốt nghiệp',
      other: 'Khác'
    };
    return labels[eventType] || eventType;
  };

  const getEventTypeColor = (eventType: string) => {
    const colors: Record<string, string> = {
      wedding: 'red',
      housewarming: 'green',
      birthday: 'blue',
      graduation: 'purple',
      other: 'default'
    };
    return colors[eventType] || 'default';
  };

  const columns = [
    {
      title: 'Tên sự kiện',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <strong>{text}</strong>
    },
    {
      title: 'Ngày diễn ra',
      dataIndex: 'event_date',
      key: 'event_date',
      render: (date: string) => (
        <Space>
          <CalendarOutlined />
          {dayjs(date).format('DD/MM/YYYY')}
        </Space>
      ),
      sorter: (a: Event, b: Event) => dayjs(a.event_date).unix() - dayjs(b.event_date).unix()
    },
    {
      title: 'Loại sự kiện',
      dataIndex: 'event_type',
      key: 'event_type',
      render: (eventType: string) => (
        <Tag color={getEventTypeColor(eventType)}>
          {getEventTypeLabel(eventType)}
        </Tag>
      )
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
      ellipsis: true
    },
    {
      title: 'Trạng thái',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Hoạt động' : 'Không hoạt động'}
        </Tag>
      )
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_, record: Event) => (
        <Space>
          <Tooltip title="Xem tiền mừng">
            <Button 
              type="link" 
              size="small"
              onClick={() => navigate(`/gifts?event_id=${record.id}`)}
            >
              Xem tiền mừng
            </Button>
          </Tooltip>
          <Tooltip title="Chỉnh sửa">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Bạn có chắc muốn xóa sự kiện này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Có"
            cancelText="Không"
          >
            <Tooltip title="Xóa">
              <Button 
                type="link" 
                danger 
                icon={<DeleteOutlined />} 
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className={styles.container}>
      <Card className={styles.header}>
        <div className={styles.headerContent}>
          <Title level={2} className={styles.title}>
            <CalendarOutlined /> Quản lý Sự kiện
          </Title>
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchEvents}
              loading={loading}
            >
              Làm mới
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Tạo sự kiện
            </Button>
          </Space>
        </div>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={events}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} của ${total} sự kiện`
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      <Modal
        title={editingEvent ? 'Chỉnh sửa sự kiện' : 'Tạo sự kiện mới'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            event_type: 'other'
          }}
        >
          <Form.Item
            name="name"
            label="Tên sự kiện"
            rules={[{ required: true, message: 'Vui lòng nhập tên sự kiện' }]}
          >
            <Input placeholder="Ví dụ: Lễ cưới Minh & Anh" />
          </Form.Item>

          <Form.Item
            name="event_date"
            label="Ngày diễn ra"
            rules={[{ required: true, message: 'Vui lòng chọn ngày diễn ra' }]}
          >
            <DatePicker 
              style={{ width: '100%' }} 
              format="DD/MM/YYYY"
              placeholder="Chọn ngày"
            />
          </Form.Item>

          <Form.Item
            name="event_type"
            label="Loại sự kiện"
            rules={[{ required: true, message: 'Vui lòng chọn loại sự kiện' }]}
          >
            <Select placeholder="Chọn loại sự kiện">
              <Option value="wedding">Đám cưới</Option>
              <Option value="housewarming">Tân gia</Option>
              <Option value="birthday">Sinh nhật</Option>
              <Option value="graduation">Tốt nghiệp</Option>
              <Option value="other">Khác</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Mô tả"
          >
            <TextArea 
              rows={3} 
              placeholder="Mô tả chi tiết về sự kiện (tùy chọn)"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingEvent ? 'Cập nhật' : 'Tạo sự kiện'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Hủy
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EventsPage; 